import client from "./client";
import {
  AdminProjectResponse,
  CreatePWClassificationRequest,
  CreatePWFringeOutputGroupRequest,
  CreatePWFringeTypeRequest,
  EmployeePWClassificationRecord,
  EmployeePWClassificationResponse,
  PatchPWClassificationRequest,
  PatchPWFringeOutputGroupRequest,
  PatchPWFringeTypeRequest,
  PostEmployeePWClassificationRequest,
  PostPWClassificationRateRequest,
  PutPWClassificationRateRequest,
  PWClassification,
  PWClassificationRateGroup,
  PWClassificationRateRecord,
  PWFringeOutputGroup,
  PWFringeType,
  ProjectUpdateRequest,
} from "../interfaces/PrevailingWage";

function getErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as { response?: { data?: { message?: string } } }).response;
    if (r?.data?.message) return r.data.message;
  }
  if (err instanceof Error) return err.message;
  return "Request failed";
}

export async function getPWFringeTypes(activeOnly?: boolean): Promise<PWFringeType[]> {
  const response = await client.get<PWFringeType[]>("admin/pw/fringeTypes", {
    params: activeOnly === undefined ? {} : { activeOnly },
  });
  return response.data;
}

export async function createPWFringeType(body: CreatePWFringeTypeRequest): Promise<PWFringeType> {
  const response = await client.post<PWFringeType>("admin/pw/fringeTypes", body);
  return response.data;
}

export async function patchPWFringeType(
  id: number,
  body: PatchPWFringeTypeRequest
): Promise<PWFringeType> {
  const response = await client.patch<PWFringeType>(`admin/pw/fringeTypes/${id}`, body);
  return response.data;
}

export async function activatePWFringeType(id: number): Promise<{ pwFringeTypeID: number; active: boolean }> {
  const response = await client.post<{ pwFringeTypeID: number; active: boolean }>(
    `admin/pw/fringeTypes/${id}/activate`
  );
  return response.data;
}

export async function deactivatePWFringeType(id: number): Promise<{ pwFringeTypeID: number; active: boolean }> {
  const response = await client.post<{ pwFringeTypeID: number; active: boolean }>(
    `admin/pw/fringeTypes/${id}/deactivate`
  );
  return response.data;
}

export async function getPWClassifications(activeOnly?: boolean): Promise<PWClassification[]> {
  const response = await client.get<PWClassification[]>("admin/pw/classifications", {
    params: activeOnly === undefined ? {} : { activeOnly },
  });
  return response.data;
}

export async function createPWClassification(body: CreatePWClassificationRequest): Promise<PWClassification> {
  const response = await client.post<PWClassification>("admin/pw/classifications", body);
  return response.data;
}

export async function patchPWClassification(
  id: number,
  body: PatchPWClassificationRequest
): Promise<PWClassification> {
  const response = await client.patch<PWClassification>(`admin/pw/classifications/${id}`, body);
  return response.data;
}

export async function activatePWClassification(
  id: number
): Promise<{ pwClassificationID: number; active: boolean }> {
  const response = await client.post<{ pwClassificationID: number; active: boolean }>(
    `admin/pw/classifications/${id}/activate`
  );
  return response.data;
}

export async function deactivatePWClassification(
  id: number
): Promise<{ pwClassificationID: number; active: boolean }> {
  const response = await client.post<{ pwClassificationID: number; active: boolean }>(
    `admin/pw/classifications/${id}/deactivate`
  );
  return response.data;
}

export async function putAdminProject(
  projectID: number,
  body: ProjectUpdateRequest
): Promise<AdminProjectResponse> {
  const response = await client.put<AdminProjectResponse>(`admin/projects/${projectID}`, body);
  return response.data;
}

export async function getProjectPWRates(projectID: number): Promise<PWClassificationRateGroup[]> {
  const response = await client.get<PWClassificationRateGroup[]>(
    `admin/projects/${projectID}/pwRates`
  );
  return response.data;
}

export async function postProjectPWRate(
  projectID: number,
  body: PostPWClassificationRateRequest
): Promise<PWClassificationRateRecord> {
  const response = await client.post<PWClassificationRateRecord>(
    `admin/projects/${projectID}/pwRates`,
    body
  );
  return response.data;
}

export async function putProjectPWRate(
  projectID: number,
  rateID: number,
  body: PutPWClassificationRateRequest
): Promise<PWClassificationRateRecord> {
  const response = await client.put<PWClassificationRateRecord>(
    `admin/projects/${projectID}/pwRates/${rateID}`,
    body
  );
  return response.data;
}

export async function getEmployeePWClassification(
  employeeID: number
): Promise<EmployeePWClassificationResponse> {
  const response = await client.get<EmployeePWClassificationResponse>(
    `admin/employees/${employeeID}/pwClassification`
  );
  return response.data;
}

export async function postEmployeePWClassification(
  employeeID: number,
  body: PostEmployeePWClassificationRequest
): Promise<EmployeePWClassificationRecord> {
  const response = await client.post<EmployeePWClassificationRecord>(
    `admin/employees/${employeeID}/pwClassification`,
    body
  );
  return response.data;
}

export async function getPWFringeOutputGroups(): Promise<PWFringeOutputGroup[]> {
  const response = await client.get<PWFringeOutputGroup[]>("admin/pw/fringeOutputGroups");
  return response.data;
}

export async function createPWFringeOutputGroup(
  body: CreatePWFringeOutputGroupRequest
): Promise<PWFringeOutputGroup> {
  const response = await client.post<PWFringeOutputGroup>("admin/pw/fringeOutputGroups", body);
  return response.data;
}

export async function patchPWFringeOutputGroup(
  id: number,
  body: PatchPWFringeOutputGroupRequest
): Promise<PWFringeOutputGroup> {
  const response = await client.patch<PWFringeOutputGroup>(`admin/pw/fringeOutputGroups/${id}`, body);
  return response.data;
}

export async function deletePWFringeOutputGroup(id: number): Promise<void> {
  try {
    await client.delete(`admin/pw/fringeOutputGroups/${id}`);
  } catch (err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status === 409) {
      throw Object.assign(new Error("CONFLICT"), { isConflict: true });
    }
    throw err;
  }
}

export { getErrorMessage };
